import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';
import { LoadScript, GoogleMap, Marker, DirectionsRenderer, InfoWindow } from '@react-google-maps/api';
import { BRANCH_LOCATION, calculateDistance } from '../config/branchLocation';
import { GOOGLE_MAPS_API_KEY, DEFAULT_MAP_OPTIONS } from '../config/googleMapsConfig';
import { useLanguage } from '../contexts/LanguageContext';

interface DeliveryMapProps {
  deliveryAddress: string;
  deliveryPostcode: string;
  deliveryCity: string;
  deliveryLat?: number;
  deliveryLng?: number;
}

// Static libraries array
const GOOGLE_MAPS_LIBRARIES: ("places" | "drawing" | "geometry" | "visualization")[] = ['places'];

/**
 * Simplified DeliveryMap Component
 * Clean, standard Google Maps implementation
 */
const DeliveryMap: React.FC<DeliveryMapProps> = ({
  deliveryAddress,
  deliveryPostcode,
  deliveryCity,
  deliveryLat,
  deliveryLng,
}) => {
  const { t } = useLanguage();
  const [directions, setDirections] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [scriptError, setScriptError] = useState<string | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<'branch' | 'delivery' | null>(null);
  const directionsServiceRef = useRef<any>(null);
  const mapRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate distance and duration
  const distance =
    deliveryLat && deliveryLng && !isNaN(deliveryLat) && !isNaN(deliveryLng)
      ? calculateDistance(
          BRANCH_LOCATION.latitude,
          BRANCH_LOCATION.longitude,
          deliveryLat,
          deliveryLng
        )
      : null;
  
  const duration = distance ? Math.round(distance * 2) : null;

  // Calculate map center - midpoint between branch and delivery
  const mapCenter = useMemo(() => {
    if (deliveryLat && deliveryLng && !isNaN(deliveryLat) && !isNaN(deliveryLng)) {
      return {
        lat: (BRANCH_LOCATION.latitude + deliveryLat) / 2,
        lng: (BRANCH_LOCATION.longitude + deliveryLng) / 2,
      };
    }
    return { lat: BRANCH_LOCATION.latitude, lng: BRANCH_LOCATION.longitude };
  }, [deliveryLat, deliveryLng]);

  // Get directions when coordinates are available
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) return;
    if (!mapLoaded) return;
    if (!deliveryLat || !deliveryLng || isNaN(deliveryLat) || isNaN(deliveryLng)) return;
    if (!directionsServiceRef.current) return;
    
    const google = (window as any).google;
    if (!google || !google.maps) return;

    console.log('🚗 [MAP] Requesting directions...');
    setLoading(true);
    
    const origin = new google.maps.LatLng(BRANCH_LOCATION.latitude, BRANCH_LOCATION.longitude);
    const destination = new google.maps.LatLng(deliveryLat, deliveryLng);

    directionsServiceRef.current.route(
      {
        origin,
        destination,
        travelMode: google.maps.TravelMode.DRIVING,
        unitSystem: google.maps.UnitSystem.METRIC,
      },
      (result: any, status: any) => {
        setLoading(false);
        if (status === google.maps.DirectionsStatus.OK && result) {
          setDirections(result);
          console.log('✅ [MAP] Directions loaded');
        } else {
          console.warn('⚠️ [MAP] Directions failed:', status);
          setDirections(null);
        }
      }
    );
  }, [mapLoaded, deliveryLat, deliveryLng]);

  // Initialize map and directions service
  const onMapLoad = React.useCallback((map: any) => {
    console.log('✅ [MAP] Map loaded');
    mapRef.current = map;
    setMapLoaded(true);
    
    const google = (window as any).google;
    if (google && google.maps) {
      directionsServiceRef.current = new google.maps.DirectionsService();
      
      // CRITICAL: Multiple resize triggers to ensure tiles load
      // This is needed because the map might be in a dialog that animates open
      const triggerResize = () => {
        if (mapRef.current) {
          const mapDiv = mapRef.current.getDiv();
          if (mapDiv) {
            const rect = mapDiv.getBoundingClientRect();
            console.log('🗺️ [MAP] Map div dimensions:', rect.width, 'x', rect.height);
            
            if (rect.width > 0 && rect.height > 0) {
              google.maps.event.trigger(mapRef.current, 'resize');
              console.log('✅ [MAP] Resize triggered');
            } else {
              console.warn('⚠️ [MAP] Map div has zero dimensions, retrying...');
            }
          }
        }
      };
      
      // Trigger resize multiple times with increasing delays
      setTimeout(triggerResize, 100);
      setTimeout(triggerResize, 300);
      setTimeout(triggerResize, 600);
      setTimeout(triggerResize, 1000);
    }
  }, []);

  // Fit bounds to show both locations
  useEffect(() => {
    if (!mapRef.current || !deliveryLat || !deliveryLng) return;
    if (isNaN(deliveryLat) || isNaN(deliveryLng)) return;
    if (!mapLoaded) return;
    
    const google = (window as any).google;
    if (!google || !google.maps) return;

    // Wait a bit for dialog animation to complete
    const timer = setTimeout(() => {
      // Trigger resize first to ensure map container has proper dimensions
      google.maps.event.trigger(mapRef.current, 'resize');
      
      // Simple fitBounds - let Google Maps handle it
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(new google.maps.LatLng(BRANCH_LOCATION.latitude, BRANCH_LOCATION.longitude));
      bounds.extend(new google.maps.LatLng(deliveryLat, deliveryLng));
      
      mapRef.current.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      });
      
      // Trigger another resize after fitBounds to ensure tiles load
      setTimeout(() => {
        google.maps.event.trigger(mapRef.current, 'resize');
      }, 200);
      
      console.log('✅ [MAP] Bounds fitted');
    }, 500);
    
    return () => clearTimeout(timer);
  }, [mapLoaded, deliveryLat, deliveryLng]);

  // Handle window resize and container visibility
  useEffect(() => {
    if (!mapLoaded) return;
    
    const handleResize = () => {
      if (mapRef.current) {
        const google = (window as any).google;
        if (google && google.maps && google.maps.event) {
          google.maps.event.trigger(mapRef.current, 'resize');
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    // Use IntersectionObserver to detect when container becomes visible (e.g., dialog opens)
    let observer: IntersectionObserver | null = null;
    if (containerRef.current && mapRef.current) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && mapRef.current) {
            const google = (window as any).google;
            if (google && google.maps && google.maps.event) {
              setTimeout(() => {
                google.maps.event.trigger(mapRef.current, 'resize');
                console.log('✅ [MAP] Resize triggered via IntersectionObserver');
              }, 100);
            }
          }
        });
      }, { threshold: 0.1 });
      
      observer.observe(containerRef.current);
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [mapLoaded]);

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.9rem', color: '#1a1a1a' }}>
          {t('map.delivery_map_distance')}
        </Typography>
        <Paper
          sx={{
            p: 1.5,
            mb: 1.5,
            backgroundColor: '#f8f9fa',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {'🗺️ ' + t('map.api_key_required')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.9rem', color: '#1a1a1a' }}>
        {t('map.delivery_map_distance')}
      </Typography>
      
      {(distance !== null || (deliveryAddress && deliveryPostcode && deliveryCity)) && (
        <Paper
          sx={{
            p: 1.5,
            mb: 1.5,
            backgroundColor: '#f8f9fa',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
          }}
        >
          {distance !== null ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('map.distance')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#ff6b35' }}>
                  {distance.toFixed(1)} km
                </Typography>
              </Box>
              {duration !== null && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('map.estimated_drive_time')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {duration} {t('map.minutes')}
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('map.loading_coordinates')}
            </Typography>
          )}
        </Paper>
      )}

      {/* Google Maps */}
      {(deliveryLat && deliveryLng) || (deliveryAddress || deliveryPostcode || deliveryCity) ? (
        <LoadScript
          googleMapsApiKey={GOOGLE_MAPS_API_KEY}
          libraries={GOOGLE_MAPS_LIBRARIES}
          onLoad={() => {
            console.log('✅ [MAP] LoadScript loaded');
            const google = (window as any).google;
            if (google && google.maps) {
              setMapLoaded(true);
            }
          }}
          onError={(error) => {
            console.error('❌ [MAP] LoadScript error:', error);
            setScriptError('Failed to load Google Maps');
          }}
          loadingElement={
            <Box
              sx={{
                height: 350,
                width: '100%',
                borderRadius: 2,
                border: '2px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          {scriptError ? (
            <Paper
              sx={{
                p: 2,
                textAlign: 'center',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
              }}
            >
              <Typography variant="body2" color="error">
                {scriptError}
              </Typography>
            </Paper>
          ) : (
            <Box
              ref={containerRef}
              sx={{
                height: 350,
                width: '100%',
                minHeight: 350,
                borderRadius: 2,
                border: '2px solid #e0e0e0',
                overflow: 'hidden',
                position: 'relative',
                display: 'block',
              }}
            >
              <GoogleMap
                mapContainerStyle={{ 
                  width: '100%', 
                  height: '100%',
                  minHeight: '350px',
                  display: 'block',
                  position: 'relative',
                }}
                center={mapCenter}
                zoom={deliveryLat && deliveryLng ? 12 : 13}
                onLoad={onMapLoad}
                options={{
                  ...DEFAULT_MAP_OPTIONS,
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: true,
                  gestureHandling: 'auto',
                  draggable: true,
                  scrollwheel: true,
                }}
              >
                {/* Branch Location Marker — Saheeh logo */}
                <Marker
                  position={{ lat: BRANCH_LOCATION.latitude, lng: BRANCH_LOCATION.longitude }}
                  icon={(() => {
                    const google = (window as any).google;
                    if (google && google.maps && google.maps.Size && google.maps.Point) {
                      return {
                        url: '/saheeh_logo_w.svg',
                        scaledSize: new google.maps.Size(44, 44),
                        anchor: new google.maps.Point(22, 44),
                      };
                    }
                    return undefined;
                  })()}
                  title={BRANCH_LOCATION.name}
                  onClick={() => setSelectedMarker('branch')}
                >
                  {selectedMarker === 'branch' && (
                    <InfoWindow onCloseClick={() => setSelectedMarker(null)}>
                      <div style={{ color: '#000', fontFamily: 'Roboto, Arial, sans-serif', minWidth: 180 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#000', marginBottom: 4 }}>
                          {BRANCH_LOCATION.name}
                        </div>
                        <div style={{ fontSize: 13, color: '#000' }}>
                          {BRANCH_LOCATION.address}
                        </div>
                        <div style={{ fontSize: 13, color: '#000' }}>
                          {BRANCH_LOCATION.postcode} {BRANCH_LOCATION.city}
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                </Marker>

                {/* Delivery Location Marker */}
                {deliveryLat && deliveryLng && !isNaN(deliveryLat) && !isNaN(deliveryLng) && (
                  <>
                    <Marker
                      position={{ lat: deliveryLat, lng: deliveryLng }}
                      icon={(() => {
                        const google = (window as any).google;
                        if (google && google.maps && google.maps.Size && google.maps.Point) {
                          return {
                            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                              <svg width="50" height="64" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 64">
                                <defs>
                                  <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                                    <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.35"/>
                                  </filter>
                                </defs>
                                <path filter="url(#shadow)" d="M25 2 C12 2 2 12 2 25 C2 40 25 62 25 62 C25 62 48 40 48 25 C48 12 38 2 25 2 Z"
                                      fill="#ff6b35" stroke="#ffffff" stroke-width="3"/>
                                <circle cx="25" cy="25" r="11" fill="#ffffff"/>
                                <path d="M25 17 L33 25 L31 25 L31 32 L27 32 L27 27 L23 27 L23 32 L19 32 L19 25 L17 25 Z"
                                      fill="#ff6b35"/>
                              </svg>
                            `),
                            scaledSize: new google.maps.Size(50, 64),
                            anchor: new google.maps.Point(25, 64),
                          };
                        }
                        return undefined;
                      })()}
                      title={t('map.delivery_address')}
                      onClick={() => setSelectedMarker('delivery')}
                    >
                      {selectedMarker === 'delivery' && (
                        <InfoWindow onCloseClick={() => setSelectedMarker(null)}>
                          <div style={{ color: '#000', fontFamily: 'Roboto, Arial, sans-serif', minWidth: 180 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: '#000', marginBottom: 4 }}>
                              {t('map.delivery_address')}
                            </div>
                            <div style={{ fontSize: 13, color: '#000' }}>
                              {deliveryAddress}
                            </div>
                            <div style={{ fontSize: 13, color: '#000' }}>
                              {deliveryPostcode} {deliveryCity}
                            </div>
                          </div>
                        </InfoWindow>
                      )}
                    </Marker>

                    {/* Directions Route */}
                    {directions && (
                      <DirectionsRenderer
                        directions={directions}
                        options={{
                          polylineOptions: {
                            strokeColor: '#ff6b35',
                            strokeOpacity: 0.8,
                            strokeWeight: 4,
                          },
                          suppressMarkers: true,
                        }}
                      />
                    )}

                    {loading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          padding: '8px 12px',
                          borderRadius: 1,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          zIndex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <CircularProgress size={16} />
                        <Typography variant="caption">
                          {t('map.calculating_route')}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </GoogleMap>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mt: 1,
                  textAlign: 'center',
                }}
              >
                {'💡 ' + t('map.map_tip')}
              </Typography>
            </Box>
          )}
        </LoadScript>
      ) : null}
    </Box>
  );
};

export default DeliveryMap;
